import React, { useEffect, useMemo, useState } from 'react';
import './LoginPage.css';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import SharkHeader from '../components/SharkHeader';
import { FaUser, FaLock, FaGlobeAmericas } from 'react-icons/fa';
import { getLocalizedText } from '../i18n/loginTranslations';
import {
  getLinkingProviderFromRedirectUri,
  getLinkingProviderFromPageContext,
  persistLinkingProviderFromRedirectUri,
} from '../utils/linkingProvider';

const isLikelyEmail = (value) => value.includes('@');

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** Separate bases so you can later point both to one API; swap env values only. */
const APP_CONFIG = {
  googleLoginApiUrl: process.env.REACT_APP_GOOGLE_LOGIN_API || '',
  defaultLoginApiUrl: process.env.REACT_APP_ALEXA_LOGIN_API_DEFAULT || '',
  euLoginApiUrl: process.env.REACT_APP_ALEXA_LOGIN_API_EU || process.env.REACT_APP_ALEXA_LOGIN_API_DEFAULT || '',
  naRedirectUri: process.env.REACT_APP_ALEXA_REDIRECT_URI_NA || '',
  /** Japan Alexa linking uses a different host; same NA login API as pitangui. */
  alexaRedirectUriJp: process.env.REACT_APP_ALEXA_REDIRECT_URI_JP || '',
  euRedirectUri: process.env.REACT_APP_ALEXA_REDIRECT_URI_EU || '',
};

const normalizeUri = (value) => (value || '').trim().replace(/\/+$/, '').toLowerCase();

const getAlexaLoginApiUrlForRedirectUri = (redirectUri) => {
  const normalized = normalizeUri(redirectUri);
  if (normalized === normalizeUri(APP_CONFIG.euRedirectUri)) {
    return APP_CONFIG.euLoginApiUrl;
  }
  if (normalized === normalizeUri(APP_CONFIG.naRedirectUri)) {
    return APP_CONFIG.defaultLoginApiUrl;
  }
  if (APP_CONFIG.alexaRedirectUriJp && normalized === normalizeUri(APP_CONFIG.alexaRedirectUriJp)) {
    return APP_CONFIG.defaultLoginApiUrl;
  }
  return APP_CONFIG.defaultLoginApiUrl;
};

/**
 * `region` is a routing/labelling claim sent to the login APIs; it is NOT a country code.
 * 'AZ' is the ANZ region (Australia + New Zealand) — two letters to match the existing
 * 'NA'/'EU' convention. It is deliberately not ISO-3166 'AZ' (Azerbaijan): `region` and
 * `country_code` ride in the same request but are separate namespaces, so a future
 * Azerbaijan row would be `{ code: 'AZ', region: 'EU' }` and must not be "reconciled"
 * with this value. US is pinned first (it is the default selection); the rest are
 * alphabetical by name.
 */
const COUNTRIES = [
  { code: 'US', name: 'United States', region: 'NA' },
  { code: 'AU', name: 'Australia', region: 'AZ' },
  { code: 'AT', name: 'Austria', region: 'EU' },
  { code: 'BE', name: 'Belgium', region: 'EU' },
  { code: 'BG', name: 'Bulgaria', region: 'EU' },
  { code: 'CA', name: 'Canada', region: 'NA' },
  { code: 'CN', name: 'China', region: 'NA' },
  { code: 'HR', name: 'Croatia', region: 'EU' },
  { code: 'CY', name: 'Cyprus', region: 'EU' },
  { code: 'CZ', name: 'Czechia', region: 'EU' },
  { code: 'DK', name: 'Denmark', region: 'EU' },
  { code: 'EE', name: 'Estonia', region: 'EU' },
  { code: 'FI', name: 'Finland', region: 'EU' },
  { code: 'FR', name: 'France', region: 'EU' },
  { code: 'DE', name: 'Germany', region: 'EU' },
  { code: 'GE', name: 'Georgia', region: 'EU' },
  { code: 'GR', name: 'Greece', region: 'EU' },
  { code: 'HU', name: 'Hungary', region: 'EU' },
  { code: 'IS', name: 'Iceland', region: 'EU' },
  { code: 'IE', name: 'Ireland', region: 'EU' },
  { code: 'IT', name: 'Italy', region: 'EU' },
  { code: 'JP', name: 'Japan', region: 'NA' },
  { code: 'LV', name: 'Latvia', region: 'EU' },
  { code: 'LI', name: 'Liechtenstein', region: 'EU' },
  { code: 'LT', name: 'Lithuania', region: 'EU' },
  { code: 'LU', name: 'Luxembourg', region: 'EU' },
  { code: 'MT', name: 'Malta', region: 'EU' },
  { code: 'MX', name: 'Mexico', region: 'NA' },
  { code: 'NL', name: 'Netherlands', region: 'EU' },
  { code: 'NZ', name: 'New Zealand', region: 'AZ' },
  { code: 'NO', name: 'Norway', region: 'EU' },
  { code: 'PL', name: 'Poland', region: 'EU' },
  { code: 'PT', name: 'Portugal', region: 'EU' },
  { code: 'RO', name: 'Romania', region: 'EU' },
  { code: 'RS', name: 'Serbia', region: 'EU' },
  { code: 'SK', name: 'Slovakia', region: 'EU' },
  { code: 'SI', name: 'Slovenia', region: 'EU' },
  { code: 'KR', name: 'South Korea', region: 'NA' },
  { code: 'ES', name: 'Spain', region: 'EU' },
  { code: 'SE', name: 'Sweden', region: 'EU' },
  { code: 'CH', name: 'Switzerland', region: 'EU' },
  { code: 'UA', name: 'Ukraine', region: 'EU' },
  { code: 'GB', name: 'United Kingdom', region: 'EU' },
];

const COUNTRY_REGION_BY_CODE = COUNTRIES.reduce((accumulator, country) => {
  accumulator[country.code] = country.region;
  return accumulator;
}, {});

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const linkingProvider = useMemo(() => getLinkingProviderFromPageContext(search), [search]);
  const t = useMemo(() => getLocalizedText(search, navigator, linkingProvider), [search, linkingProvider]);

  const authProviders = [
    { id: 'google', label: 'Google', url: process.env.REACT_APP_GOOGLE_SIGNIN_URL || '' },
    { id: 'amazon', label: 'Amazon', url: process.env.REACT_APP_AMAZON_SIGNIN_URL || '' },
    { id: 'facebook', label: 'Facebook', url: process.env.REACT_APP_FACEBOOK_SIGNIN_URL || '' },
  ].filter((provider) => Boolean(provider.url));

  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (window.location.protocol !== 'https:' && !isLocalhost) {
      window.location.replace(`https://${window.location.host}${window.location.pathname}${window.location.search}`);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedUsername = username.trim();
    const hasPasswordEdgeSpaces = password !== password.trim();

    if (!trimmedUsername || !password) {
      setError(t.missingFields);
      return;
    }

    if (username !== trimmedUsername) {
      setError(t.usernameTrim);
      return;
    }

    if (hasPasswordEdgeSpaces) {
      setError(t.passwordSpaces);
      return;
    }

    if (isLikelyEmail(trimmedUsername) && !isValidEmail(trimmedUsername)) {
      setError(t.invalidEmail);
      return;
    }

    setIsProcessing(true);

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const clientId = queryParams.get('clientId') || queryParams.get('client_id') || '';
      const responseType = queryParams.get('response_type') || '';
      const scope = queryParams.get('scope') || '';
      const redirectUri = queryParams.get('redirect_uri') || '';
      const state = queryParams.get('state') || '';

      const region = COUNTRY_REGION_BY_CODE[countryCode] || 'NA';
      const linkingProvider = getLinkingProviderFromRedirectUri(redirectUri);

      if (linkingProvider === 'google') {
        if (!APP_CONFIG.googleLoginApiUrl) {
          setError(t.configError);
          setIsProcessing(false);
          return;
        }
        if (!APP_CONFIG.googleLoginApiUrl.startsWith('https://')) {
          throw new Error('Insecure transport blocked');
        }
        // Preserve all incoming OAuth query params (VEITA-style); append logging / routing fields.
        const outboundQuery = new URLSearchParams(window.location.search);
        outboundQuery.set('region', region);
        outboundQuery.set('country_code', countryCode);
        outboundQuery.set('provider', 'google');
        const requestUrl = `${APP_CONFIG.googleLoginApiUrl}?${outboundQuery.toString()}`;
        const response = await axios.post(requestUrl, {
          username: trimmedUsername,
          password,
        });
        const redirectURI = response.data.redirectURI;
        sessionStorage.setItem('redirectURI', redirectURI);
        sessionStorage.setItem('voiceLoginReturnSearch', window.location.search || '');
        persistLinkingProviderFromRedirectUri(redirectUri);
        navigate('/consent');
      } else {
        if (!APP_CONFIG.defaultLoginApiUrl || !APP_CONFIG.naRedirectUri || !APP_CONFIG.euRedirectUri) {
          setError(t.configError);
          setIsProcessing(false);
          return;
        }

        const requestUrl = getAlexaLoginApiUrlForRedirectUri(redirectUri);
        if (!requestUrl.startsWith('https://')) {
          throw new Error('Insecure transport blocked');
        }

        const requestPayload = {
          username: trimmedUsername,
          password,
          country_code: countryCode,
          region,
          provider: 'alexa',
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType || 'code',
          state,
        };

        if (scope) {
          requestPayload.scope = scope;
        }

        const response = await axios.post(requestUrl, requestPayload);

        const redirectURI = response.data.redirectURI;
        sessionStorage.setItem('redirectURI', redirectURI);
        sessionStorage.setItem('voiceLoginReturnSearch', window.location.search || '');
        persistLinkingProviderFromRedirectUri(redirectUri);
        navigate('/consent');
      }
    } catch (err) {
      setError(t.invalidCredentials);
    } finally {
      setIsProcessing(false);
    }
  };


  //   const handleBack = () => {
  //     window.history.back(); // Go to previous page
  //   };

  return (
    <div className="login-shell">
      <div className="container">
        {/* <button className="back-button" onClick={handleBack}>←</button> */}
        <SharkHeader />
        <h1 className="title">{t.title}</h1>
        <p className="subtitle">{t.subtitle}</p>
        <p className="instruction">{t.instruction}</p>
        <p className="region-note">
          Select the same country you chose in the SharkClean mobile app when you originally signed up.
        </p>
        <form className="login-form" onSubmit={handleLogin} autoComplete="off">
          <div className="input-group select-group">
            <FaGlobeAmericas className="input-icon" />
            <select
              aria-label="Country"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
            >
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <FaUser className="input-icon" />
            <input
              type="text"
              placeholder={t.usernameLabel}
              aria-label={t.usernameLabel}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
              maxLength={320}
            />
          </div>
          <div className="input-group">
            <FaLock className="input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t.passwordLabel}
              aria-label={t.passwordLabel}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={128}
            />
          </div>
          <div className="show-password">
            <input
              type="checkbox"
              id="showPassword"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            <label htmlFor="showPassword">{t.showPassword}</label>
          </div>
          {error && (
            <div className="error-box" role="alert" aria-live="polite">
              <div className="error-title">{t.loginFailedTitle}</div>
              <div className="error-description">{error}</div>
            </div>
          )}
          <button type="submit" className="login-button" disabled={isProcessing}>
            {isProcessing ? t.processing : t.login}
          </button>
          {authProviders.length > 0 && (
            <div className="provider-section">
              <p className="provider-title">{t.otherMethods}</p>
              <div className="provider-buttons">
                {authProviders.map((provider) => (
                  <a key={provider.id} href={provider.url} className="provider-link">
                    {provider.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
